import React, { useState, useMemo } from 'react';
import './AdjustmentPreviewModal.css';

const AdjustmentPreviewModal = ({ 
  originalSections, 
  adjustedSections, 
  onAccept, 
  onCancel,
  isOpen 
}) => {
  const [acceptedSections, setAcceptedSections] = useState({});

  // Filter to only show sections that have actually changed
  const changedSections = useMemo(() => {
    const changes = [];
    originalSections.forEach((originalSection, index) => {
      const adjustedSection = adjustedSections[index];
      if (adjustedSection && 
          originalSection.title === adjustedSection.title &&
          originalSection.content.trim() !== adjustedSection.content.trim()) {
        changes.push({
          originalIndex: index,
          original: originalSection,
          adjusted: adjustedSection
        });
      }
    });
    return changes;
  }, [originalSections, adjustedSections]);

  if (!isOpen) return null;

  // If no changes, show message
  if (changedSections.length === 0) {
    return (
      <div className="adjustment-preview-overlay">
        <div className="adjustment-preview-modal">
          <div className="preview-header">
            <h2>Inga ändringar gjordes</h2>
            <button className="close-button" onClick={onCancel}>✕</button>
          </div>
          <div className="preview-content" style={{ padding: '40px', textAlign: 'center' }}>
            <p>AI:n gjorde inga ändringar i journalen baserat på din instruktion.</p>
          </div>
          <div className="preview-footer">
            <button className="cancel-btn" onClick={onCancel}>
              Stäng
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleAcceptSection = (changedIndex) => {
    setAcceptedSections(prev => ({
      ...prev,
      [changedIndex]: !prev[changedIndex]
    }));
  };

  const handleAcceptAll = () => {
    // Mark all sections as accepted
    const allAccepted = {};
    changedSections.forEach((_, changedIndex) => {
      allAccepted[changedIndex] = true;
    });
    setAcceptedSections(allAccepted);
    
    // Immediately confirm with all changes accepted
    const finalSections = [...originalSections];
    changedSections.forEach((changeData, changedIndex) => {
      finalSections[changeData.originalIndex] = changeData.adjusted;
    });
    
    onAccept(finalSections);
    setAcceptedSections({});
  };

  const handleRejectAll = () => {
    setAcceptedSections({});
  };

  const handleConfirm = () => {
    // Start with original sections
    const finalSections = [...originalSections];
    
    // Apply accepted changes
    changedSections.forEach((changeData, changedIndex) => {
      if (acceptedSections[changedIndex]) {
        finalSections[changeData.originalIndex] = changeData.adjusted;
      }
    });
    
    onAccept(finalSections);
    setAcceptedSections({});
  };

  const handleCancel = () => {
    setAcceptedSections({});
    onCancel();
  };

  return (
    <div className="adjustment-preview-overlay">
      <div className="adjustment-preview-modal">
        <div className="preview-header">
          <h2>Förhandsgranska ändringar</h2>
          <button className="close-button" onClick={handleCancel}>✕</button>
        </div>
        
        <div className="preview-actions">
          <button className="accept-all-btn" onClick={handleAcceptAll}>
            Acceptera alla ändringar
          </button>
          <button className="reject-all-btn" onClick={handleRejectAll}>
            Avvisa alla ändringar
          </button>
        </div>

        <div className="preview-content">
          {changedSections.map((changeData, changedIndex) => {
            const isAccepted = acceptedSections[changedIndex];
            
            return (
              <div key={changedIndex} className="section-comparison">
                <div className="section-header">
                  <h3>{changeData.original.title}</h3>
                  <label className="section-toggle">
                    <input
                      type="checkbox"
                      checked={isAccepted || false}
                      onChange={() => handleAcceptSection(changedIndex)}
                    />
                    <span className="toggle-label">
                      {isAccepted ? 'Accepterad' : 'Ej accepterad'}
                    </span>
                  </label>
                </div>
                
                <div className="comparison-container">
                  <div className="original-section">
                    <div className="section-label original-label">Före</div>
                    <div className={`section-text ${isAccepted ? 'dimmed' : ''}`}>
                      {changeData.original.content}
                    </div>
                  </div>
                  
                  <div className="adjusted-section">
                    <div className="section-label adjusted-label">Efter</div>
                    <div className={`section-text ${!isAccepted ? 'dimmed' : ''}`}>
                      {changeData.adjusted.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="preview-footer">
          <button className="cancel-btn" onClick={handleCancel}>
            Avbryt
          </button>
          <button className="confirm-btn" onClick={handleConfirm}>
            Bekräfta valda ändringar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustmentPreviewModal;